import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TimeSlot {
  start_time: string; // "YYYY-MM-DD HH:mm:ss"
  end_time: string;   // "YYYY-MM-DD HH:mm:ss"
}

export interface BookingCalendarTexts {
  stepDateTitle: string;
  stepDateSubtitle: string;
  stepTimeTitle: string;
  stepTimeSubtitle: string;
  stepFormTitle: string;
  monthNames: string[];
  dayAbbreviations: string[];
  todayLabel: string;
  prevMonth: string;
  nextMonth: string;
  noSlotsDate: string;
  noSlotsTime: string;
  selectTimePrompt: string;
  nameLabel: string;
  namePlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  notesLabel: string;
  notesPlaceholder: string;
  submitButton: string;
  submittingText: string;
  backToDate: string;
  backToTime: string;
  modifyTime: string;
  selectedDateLabel: string;
  selectedTimeLabel: string;
  successTitle: string;
  successMessage: string;
  loadingSlots: string;
  errorFetch: string;
  errorSubmit: string;
  retryButton: string;
  doneButton: string;
  dateNextButton: string;
}

export interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: BookingCalendarTexts;
  containerStyle?: React.CSSProperties;
  onAppointmentConfirmed?: (data: {
    customer_name: string;
    customer_phone: string;
    start_time: string;
    end_time: string;
    notes?: string;
  }) => void | Promise<void>;
}

type Step = 0 | 1 | 2;

// ─── Date helpers (string-only extraction per spec) ──────────────────────────

const extractDate = (startTime: string): string => startTime.split(' ')[0];

const extractTime = (datetime: string): string => {
  const parts = datetime.split(' ');
  return parts.length >= 2 ? parts[1].substring(0, 5) : '';
};

const todayStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const monthKey = (year: number, month: number): string => `${year}-${String(month + 1).padStart(2, '0')}`;

const dateToParts = (dateStr: string): { year: number; month: number; day: number } => {
  const [y, m, d] = dateStr.split('-');
  return { year: Number(y), month: Number(m) - 1, day: Number(d) };
};

// ─── Calendar grid generation (local-time Date, no toISOString) ──────────────

interface CalendarDay {
  date: string;       // "YYYY-MM-DD"
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isAvailable: boolean;
  isPast: boolean;
}

function generateCalendarGrid(
  year: number,
  month: number,
  availableSet: Set<string>,
  today: string,
): CalendarDay[][] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startDow = firstDay.getDay(); // 0=Sun

  const cells: CalendarDay[] = [];

  // Days from previous month
  const prevLastDay = new Date(year, month, 0).getDate();
  for (let i = startDow - 1; i >= 0; i--) {
    const d = prevLastDay - i;
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    const dateStr = `${py}-${String(pm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({
      date: dateStr,
      dayOfMonth: d,
      isCurrentMonth: false,
      isAvailable: availableSet.has(dateStr),
      isPast: dateStr < today,
    });
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({
      date: dateStr,
      dayOfMonth: d,
      isCurrentMonth: true,
      isAvailable: availableSet.has(dateStr),
      isPast: dateStr < today,
    });
  }

  // Next month days to fill 6 rows
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const nm = month === 11 ? 0 : month + 1;
    const ny = month === 11 ? year + 1 : year;
    const dateStr = `${ny}-${String(nm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({
      date: dateStr,
      dayOfMonth: d,
      isCurrentMonth: false,
      isAvailable: availableSet.has(dateStr),
      isPast: dateStr < today,
    });
  }

  // Chunk into weeks
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

// ─── Find closest available date ≥ today ─────────────────────────────────────

function findClosestAvailableDate(availableDates: string[], today: string): string | null {
  const sorted = [...availableDates].sort();
  const future = sorted.filter((d) => d >= today);
  if (future.length > 0) return future[0];
  if (sorted.length > 0) return sorted[sorted.length - 1];
  return null;
}

// ─── BookingCalendar Component ───────────────────────────────────────────────

export default function BookingCalendar({
  timeslotsApi,
  appointmentsApi,
  texts,
  containerStyle,
  onAppointmentConfirmed,
}: BookingCalendarProps) {
  // ── State ──
  const [step, setStep] = useState<Step>(0);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [calendarYear, setCalendarYear] = useState<number>(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState<number>(new Date().getMonth());
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const initializedRef = useRef(false);

  const today = useMemo(() => todayStr(), []);

  // ── Derived data ──
  const availableDates = useMemo(() => {
    const set = new Set<string>();
    for (const s of slots) set.add(extractDate(s.start_time));
    return Array.from(set).sort();
  }, [slots]);

  const availableSet = useMemo(() => new Set(availableDates), [availableDates]);

  const slotsForDate = useMemo(() => {
    if (!selectedDate) return [];
    return slots.filter((s) => extractDate(s.start_time) === selectedDate);
  }, [slots, selectedDate]);

  const weeks = useMemo(
    () => generateCalendarGrid(calendarYear, calendarMonth, availableSet, today),
    [calendarYear, calendarMonth, availableSet, today],
  );

  // ── Fetch timeslots ──
  const fetchSlots = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(timeslotsApi);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: TimeSlot[] = await res.json();
      setSlots(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }, [timeslotsApi]);

  // ── Initial load + auto-navigate calendar ──
  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Auto-navigate calendar when slots update
  useEffect(() => {
    if (slots.length === 0) return;

    const closest = findClosestAvailableDate(availableDates, today);
    if (!closest) return;

    const { month, year } = dateToParts(closest);

    if (!initializedRef.current) {
      initializedRef.current = true;
      setCalendarYear(year);
      setCalendarMonth(month);
      if (!selectedDate || !availableSet.has(selectedDate)) {
        setSelectedDate(closest);
      }
    } else {
      // On refresh, check if current month still has availability
      const currentMonthHasSlots = availableDates.some((d) => {
        const parts = dateToParts(d);
        return parts.year === calendarYear && parts.month === calendarMonth;
      });
      if (!currentMonthHasSlots) {
        setCalendarYear(year);
        setCalendarMonth(month);
      }
    }
  }, [slots, availableDates, today, calendarYear, calendarMonth, selectedDate, availableSet]);

  // ── Navigation ──
  const goToPrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarYear((y) => y - 1);
      setCalendarMonth(11);
    } else {
      setCalendarMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarYear((y) => y + 1);
      setCalendarMonth(0);
    } else {
      setCalendarMonth((m) => m + 1);
    }
  };

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSelectSlot = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setStep(2);
    setSubmitError(null);
  };

  const handleGoToStep1 = () => {
    if (selectedDate) {
      setStep(1);
      setSelectedSlot(null);
    }
  };

  const handleGoToStep0 = () => {
    setStep(0);
  };

  const handleModifyTime = () => {
    setStep(1);
  };

  // ── Submit ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const res = await fetch(appointmentsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: formName.trim(),
          customer_phone: formPhone.trim(),
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          notes: formNotes.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }

      setSuccess(true);
      // Callback for parent to persist appointment
      if (onAppointmentConfirmed) {
        try {
          await onAppointmentConfirmed({
            customer_name: formName.trim(),
            customer_phone: formPhone.trim(),
            start_time: selectedSlot.start_time,
            end_time: selectedSlot.end_time,
            notes: formNotes.trim() || undefined,
          });
        } catch (_) {
          // Parent persistence failure doesn't block the success flow
        }
      }
      // Re-fetch to update availability
      await fetchSlots();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    setStep(0);
    setSelectedDate(null);
    setSelectedSlot(null);
    setFormName('');
    setFormPhone('');
    setFormNotes('');
    setSuccess(false);
    setSubmitError(null);
  };

  const isNameValid = formName.trim().length >= 2;
  const isPhoneValid = formPhone.trim().length >= 8;
  const isFormValid = isNameValid && isPhoneValid;

  // ── Shared styles ──
  const cardClasses =
    'bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-7';
  const btnPrimaryClasses =
    'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';
  const btnSecondaryClasses =
    'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-background-200 dark:border-background-300 bg-background-50 dark:bg-background-200 text-foreground-700 dark:text-foreground-300 text-sm font-medium transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer hover:bg-background-100 dark:hover:bg-background-300';

  // ── Step indicator ──
  const stepLabels = [texts.stepDateTitle, texts.stepTimeTitle, texts.stepFormTitle];

  return (
    <div style={containerStyle}>
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {stepLabels.map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                i < step
                  ? 'bg-primary-500 text-white'
                  : i === step
                  ? 'bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-400 border-2 border-primary-500'
                  : 'bg-background-100 dark:bg-background-200 text-foreground-400 border-2 border-background-200 dark:border-background-300'
              }`}
            >
              {i < step ? (
                <i className="ri-check-line" />
              ) : (
                i + 1
              )}
            </div>
            <span
              className={`text-xs font-medium hidden sm:inline ${
                i === step ? 'text-foreground-900 dark:text-foreground-100' : 'text-foreground-400'
              }`}
            >
              {label}
            </span>
            {i < 2 && (
              <div className={`w-8 h-0.5 hidden sm:block ${i < step ? 'bg-primary-400' : 'bg-background-200 dark:bg-background-300'}`} />
            )}
          </div>
        ))}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className={`${cardClasses} flex flex-col items-center justify-center py-16`}>
          <div className="w-10 h-10 border-4 border-background-200 dark:border-background-300 border-t-primary-500 rounded-full animate-spin mb-4" />
          <p className="text-sm text-foreground-500">{texts.loadingSlots}</p>
        </div>
      )}

      {/* ── Error ── */}
      {!loading && fetchError && (
        <div className={`${cardClasses} flex flex-col items-center justify-center py-12`}>
          <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <p className="text-sm text-foreground-700 dark:text-foreground-300 mb-4 text-center">{texts.errorFetch}</p>
          <button type="button" onClick={fetchSlots} className={btnPrimaryClasses}>
            <i className="ri-refresh-line" />
            {texts.retryButton}
          </button>
        </div>
      )}

      {/* ── Step 0: Date Selection ── */}
      {!loading && !fetchError && step === 0 && (
        <div className={cardClasses}>
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">
                {texts.stepDateTitle}
              </h3>
              <p className="text-xs text-foreground-500 mt-0.5">{texts.stepDateSubtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goToPrevMonth}
                className="w-9 h-9 rounded-lg border border-background-200 dark:border-background-300 flex items-center justify-center text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 transition-colors cursor-pointer"
                aria-label={texts.prevMonth}
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="text-sm font-semibold text-foreground-900 dark:text-foreground-100 min-w-[120px] text-center">
                {texts.monthNames[calendarMonth]} {calendarYear}
              </span>
              <button
                type="button"
                onClick={goToNextMonth}
                className="w-9 h-9 rounded-lg border border-background-200 dark:border-background-300 flex items-center justify-center text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 transition-colors cursor-pointer"
                aria-label={texts.nextMonth}
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 gap-1 mb-1.5">
            {texts.dayAbbreviations.map((dow) => (
              <div
                key={dow}
                className="text-center text-[11px] font-semibold text-foreground-400 uppercase tracking-wider py-1"
              >
                {dow}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="space-y-1">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1">
                {week.map((day) => {
                  const isToday = day.date === today;
                  const isSelected = day.date === selectedDate;
                  const isClickable = day.isAvailable && !day.isPast;

                  return (
                    <button
                      key={day.date}
                      type="button"
                      disabled={!isClickable}
                      onClick={() => isClickable && handleSelectDate(day.date)}
                      className={`relative w-full aspect-square rounded-xl flex flex-col items-center justify-center text-xs font-medium transition-all duration-150 ${
                        isSelected
                          ? 'bg-primary-500 text-white shadow-md scale-105'
                          : isClickable
                          ? 'bg-primary-50 dark:bg-primary-500/8 text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-500/15 cursor-pointer'
                          : day.isCurrentMonth
                          ? 'text-foreground-300 dark:text-foreground-600 cursor-not-allowed'
                          : 'text-foreground-200 dark:text-foreground-700 cursor-not-allowed'
                      }`}
                    >
                      <span className="text-[10px] leading-none opacity-60 mb-0.5">
                        {texts.dayAbbreviations[new Date(day.date).getDay()]}
                      </span>
                      <span className="leading-none">{day.dayOfMonth}</span>
                      {isToday && !isSelected && (
                        <span className="absolute bottom-1 w-1 h-1 rounded-full bg-primary-500" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Next button */}
          {selectedDate && (
            <div className="mt-6 pt-4 border-t border-background-100 dark:border-background-200">
              <button type="button" onClick={handleGoToStep1} className={`${btnPrimaryClasses} w-full sm:w-auto`}>
                {texts.dateNextButton}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Step 1: Time Slot Selection ── */}
      {!loading && !fetchError && step === 1 && (
        <div className={cardClasses}>
          <button
            type="button"
            onClick={handleGoToStep0}
            className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors mb-5 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-sm" />
            {texts.backToDate}
          </button>

          <h3 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">
            {texts.stepTimeTitle}
          </h3>
          <p className="text-xs text-foreground-500 mb-5">
            <span className="font-medium">{texts.selectedDateLabel}:</span>{' '}
            {selectedDate}
          </p>

          {slotsForDate.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-2xl bg-background-100 dark:bg-background-200 flex items-center justify-center mx-auto mb-3">
                <i className="ri-time-line text-2xl text-foreground-400" />
              </div>
              <p className="text-sm text-foreground-500">{texts.noSlotsTime}</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-foreground-400 mb-3">{texts.selectTimePrompt}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                {slotsForDate.map((slot) => (
                  <button
                    key={slot.start_time}
                    type="button"
                    onClick={() => handleSelectSlot(slot)}
                    className="px-4 py-3 rounded-xl border border-background-200 dark:border-background-300 bg-background-50 dark:bg-background-200 text-sm font-medium text-foreground-700 dark:text-foreground-300 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/10 hover:text-primary-700 dark:hover:text-primary-400 transition-all duration-150 cursor-pointer whitespace-nowrap text-center"
                  >
                    {extractTime(slot.start_time)} - {extractTime(slot.end_time)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Step 2: Review + Form + Submit ── */}
      {!loading && !fetchError && step === 2 && !success && (
        <div className={cardClasses}>
          <button
            type="button"
            onClick={handleModifyTime}
            className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors mb-5 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-sm" />
            {texts.backToTime}
          </button>

          <h3 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-5">
            {texts.stepFormTitle}
          </h3>

          {/* Review section */}
          <div className="bg-background-50 dark:bg-background-200 rounded-xl border border-background-100 dark:border-background-300 p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                {texts.selectedTimeLabel}
              </h4>
              <button
                type="button"
                onClick={handleModifyTime}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-edit-line text-xs" />
                {texts.modifyTime}
              </button>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              <div>
                <span className="text-[11px] text-foreground-400 block">{texts.selectedDateLabel}</span>
                <span className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">
                  {selectedSlot ? extractDate(selectedSlot.start_time) : '—'}
                </span>
              </div>
              <div>
                <span className="text-[11px] text-foreground-400 block">{texts.selectedTimeLabel}</span>
                <span className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">
                  {selectedSlot ? `${extractTime(selectedSlot.start_time)} - ${extractTime(selectedSlot.end_time)}` : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate>
            {submitError && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-500/8 border border-red-200 dark:border-red-500/15 text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="booking-name" className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-1.5">
                  {texts.nameLabel} <span className="text-red-500">*</span>
                </label>
                <input
                  id="booking-name"
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={texts.namePlaceholder}
                  className="w-full px-4 py-2.5 rounded-xl border border-background-200 dark:border-background-300 bg-white dark:bg-background-100 text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="booking-phone" className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-1.5">
                  {texts.phoneLabel} <span className="text-red-500">*</span>
                </label>
                <input
                  id="booking-phone"
                  type="tel"
                  required
                  value={formPhone}
                  onChange={(e) => setFormPhone(e.target.value)}
                  placeholder={texts.phonePlaceholder}
                  className="w-full px-4 py-2.5 rounded-xl border border-background-200 dark:border-background-300 bg-white dark:bg-background-100 text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="booking-notes" className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-1.5">
                  {texts.notesLabel}
                </label>
                <textarea
                  id="booking-notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder={texts.notesPlaceholder}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-xl border border-background-200 dark:border-background-300 bg-white dark:bg-background-100 text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                />
                <p className="text-[11px] text-foreground-400 mt-1 text-right">{formNotes.length}/500</p>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-background-100 dark:border-background-200 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleModifyTime}
                className={btnSecondaryClasses}
              >
                <i className="ri-arrow-left-line" />
                {texts.backToTime}
              </button>
              <button
                type="submit"
                disabled={!isFormValid || submitting}
                className={`${btnPrimaryClasses} flex-1`}
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {texts.submittingText}
                  </>
                ) : (
                  <>
                    <i className="ri-check-line" />
                    {texts.submitButton}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Success ── */}
      {!loading && !fetchError && step === 2 && success && (
        <div className={`${cardClasses} flex flex-col items-center justify-center py-14 text-center`}>
          <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center mb-5 animate-scaleIn">
            <i className="ri-check-line text-3xl text-emerald-500" />
          </div>
          <h3 className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
            {texts.successTitle}
          </h3>
          <p className="text-sm text-foreground-500 dark:text-foreground-400 max-w-sm mb-6 leading-relaxed">
            {texts.successMessage}
          </p>
          <button type="button" onClick={handleDone} className={btnPrimaryClasses}>
            <i className="ri-check-line" />
            {texts.doneButton}
          </button>
        </div>
      )}
    </div>
  );
}